import { UserAssistanceStateRealTimeData } from 'src/generated/signalR/user-assistance-state-real-time-data';
import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { UpdateDesiredAssistanceState, UpdateUserAssistanceStates } from '../samwin-user-assistance-status/samwin-user-assistance-status.actions';
import _ from 'lodash';
import { EnabledFeaturesState } from '../../session/enabled-features/enabled-features.state';
import { SessionState } from '../../session/session.state';
import { AgentDetailsBehaviourState } from '../../dashboard/brick-wall/brick-behaviour-state/brickTypes/agent-details-behavior/agent-details-behavior.state';
import { SamwinAgentListState } from '../samwin-agent-list/samwin-agent-list.state';
import { AgentListEntryRemoteDto } from 'src/generated/signalR/agent-list-entry-remote-dto';
import { LineStateEnum } from 'src/app/samwebDescription/samwin/LineStateEnum';
import { AssistanceState } from 'src/generated/signalR/assistance-state';
import { AssistanceStateEnum } from './assistance-state-enum';
import { UserAssistanceStatesRealTimeData } from 'src/generated/signalR/user-assistance-states-real-time-data';

export interface SamwinUserAssistanceStateModel {
   assistanceStates: { [userId: number]: UserAssistanceStateRealTimeData };
   desiredUserId: number;
   desiredState: AssistanceState;
}

@State<SamwinUserAssistanceStateModel>({
   name: 'samwinUserAssistanceState',
   defaults: {
      assistanceStates: {},
      desiredUserId: null,
      desiredState: null,
   },
})
@Injectable()
export class SamwinUserAssistanceState {
   constructor(public store: Store) {}

   @Selector([SamwinUserAssistanceState])
   public static getUserAssistanceStates(state: SamwinUserAssistanceStateModel): { [userId: number]: UserAssistanceStateRealTimeData } {
      return state.assistanceStates;
   }

   @Selector([SamwinUserAssistanceState.getUserAssistanceStates])
   public static getAssistanceStateByUserIdFn(userAssistanceStates: {
      [userId: number]: UserAssistanceStateRealTimeData;
   }): (userId: number) => UserAssistanceStateRealTimeData {
      return (userId: number) => {
         return userAssistanceStates[userId];
      };
   }

   @Selector([SamwinUserAssistanceState.getUserAssistanceStates])
   public static getAssistanceStateByAssistingUserIdFn(userAssistanceStates: {
      [userId: number]: UserAssistanceStateRealTimeData;
   }): (userId: number) => UserAssistanceStateRealTimeData {
      return (userId: number) => {
         return _.values(userAssistanceStates).find((x) => x.assistingUserId === userId);
      };
   }

   @Selector([SamwinUserAssistanceState.getAssistanceStateByAssistingUserIdFn, SessionState.getAuthenticatedUserId])
   public static getMyAssistanceState(
      getAssistanceStateByAssistingUserIdFn: (userId: number) => UserAssistanceStateRealTimeData,
      myUserId: number
   ): UserAssistanceStateRealTimeData {
      return getAssistanceStateByAssistingUserIdFn(myUserId);
   }

   @Selector([SamwinUserAssistanceState.getAssistanceStateByUserIdFn, SessionState.getAuthenticatedUserId])
   public static getMyAssistedState(
      getAssistanceStateByUserIdFn: (userId: number) => UserAssistanceStateRealTimeData,
      myUserId: number
   ): UserAssistanceStateRealTimeData {
      return getAssistanceStateByUserIdFn(myUserId);
   }

   @Selector([SamwinUserAssistanceState.getMyAssistedState])
   public static getMyAssistedStateEnum(myAssistedState: UserAssistanceStateRealTimeData): AssistanceStateEnum {
      return AssistanceStateEnum.fromAssistanceStateValue(myAssistedState?.state);
   }

   @Selector([
      SessionState.getAuthenticatedUserId,
      SamwinUserAssistanceState.getMyAssistanceState,
      EnabledFeaturesState.isSupervisor,
      AgentDetailsBehaviourState.getCurrentSelectedAgentId,
      SamwinAgentListState.getAgentDetailsForUserId,
      SamwinUserAssistanceState.getAssistanceStateByUserIdFn,
   ])
   public static canBargeIn(
      myUserId: number,
      myAssistanceState: UserAssistanceStateRealTimeData,
      isSupervisor: boolean,
      selectedAgentId: number,
      getAgentDetailsForUserIdFn: (userId: number) => null | AgentListEntryRemoteDto,
      getAssistanceStateByUserIdFn: (userId: number) => UserAssistanceStateRealTimeData
   ): boolean {
      if (!isSupervisor) {
         return false;
      }

      const myAssistingState = myAssistanceState?.state ?? AssistanceState.None;
      // my assistance state is idle, ...
      if (myAssistingState === AssistanceState.None) {
         // ... but no other agent is selected
         if (_.isNil(selectedAgentId) || selectedAgentId === myUserId) {
            return false;
         }
         // ... or the selected agent is not in a call
         const callState = LineStateEnum.fromEnumValue(getAgentDetailsForUserIdFn(selectedAgentId)?.callState);
         if (!callState.isCallInProgress) {
            return false;
         }
         const assistanceState = getAssistanceStateByUserIdFn(selectedAgentId);
         // ... or another agent is already assisting
         if (
            _.isNil(assistanceState) ||
            (assistanceState?.assistingUserId !== 0 && assistanceState?.state !== AssistanceState.None && assistanceState?.assistingUserId !== myUserId)
         ) {
            return false;
         }
      }

      return true;
   }

   @Selector([SamwinUserAssistanceState.canBargeIn, AgentDetailsBehaviourState.getCurrentSelectedAgentId, SamwinUserAssistanceState.getMyAssistanceState])
   public static canBargeInToSelectedAgent(canBargeIn: boolean, selectedAgentId: number, myAssistanceState: UserAssistanceStateRealTimeData): boolean {
      if (!canBargeIn || selectedAgentId === 0) {
         return false;
      }

      return _.isNil(myAssistanceState) || myAssistanceState?.state === AssistanceState.None || myAssistanceState?.userId === selectedAgentId;
   }

   @Selector([SessionState.getAuthenticatedUserId, SamwinUserAssistanceState.getMyAssistanceState, AgentDetailsBehaviourState.getCurrentSelectedAgentId])
   public static getUserToAssistFn(
      myUserId: number,
      myAssistingState: UserAssistanceStateRealTimeData,
      selectedAgentId: number
   ): (desiredUser: number) => number {
      return (desiredUser: number) => {
         const assistedUserId = myAssistingState?.userId ?? 0;
         const isAssisting = _.includes(
            [AssistanceState.BargingIn, AssistanceState.Listening, AssistanceState.Whispering, AssistanceState.Conference],
            myAssistingState?.state
         );

         // when already assisting -> use the current user id
         if (isAssisting && assistedUserId > 0) {
            return assistedUserId;
         }

         const userId = desiredUser ?? selectedAgentId;
         // there might be no user specified or selected, nor can Agents assist themselves
         if (userId === 0 || userId === myUserId) {
            return 0;
         }
         return userId;
      };
   }

   @Selector([SamwinUserAssistanceState, SamwinUserAssistanceState.getMyAssistanceState])
   public static isListening(state: SamwinUserAssistanceStateModel, myAssistanceState: UserAssistanceStateRealTimeData): boolean {
      return myAssistanceState?.state === AssistanceState.Listening || state.desiredState === AssistanceState.Listening;
   }

   @Selector([SamwinUserAssistanceState, SamwinUserAssistanceState.getMyAssistanceState, AgentDetailsBehaviourState.getCurrentSelectedAgentId])
   public static isListeningToSelectedAgent(
      state: SamwinUserAssistanceStateModel,
      myAssistanceState: UserAssistanceStateRealTimeData,
      selectedAgentId: number
   ): boolean {
      return (
         (myAssistanceState?.userId === selectedAgentId || state.desiredUserId === selectedAgentId) &&
         (myAssistanceState?.state === AssistanceState.Listening || state.desiredState === AssistanceState.Listening)
      );
   }

   @Selector([SamwinUserAssistanceState, SamwinUserAssistanceState.getMyAssistanceState])
   public static isWhispering(state: SamwinUserAssistanceStateModel, myAssistanceState: UserAssistanceStateRealTimeData): boolean {
      return myAssistanceState?.state === AssistanceState.Whispering || state.desiredState === AssistanceState.Whispering;
   }

   @Selector([SamwinUserAssistanceState, SamwinUserAssistanceState.getMyAssistanceState, AgentDetailsBehaviourState.getCurrentSelectedAgentId])
   public static isWhisperingToSelectedAgent(
      state: SamwinUserAssistanceStateModel,
      myAssistanceState: UserAssistanceStateRealTimeData,
      selectedAgentId: number
   ): boolean {
      return (
         (myAssistanceState?.userId === selectedAgentId || state.desiredUserId === selectedAgentId) &&
         (myAssistanceState?.state === AssistanceState.Whispering || state.desiredState === AssistanceState.Whispering)
      );
   }

   @Selector([SamwinUserAssistanceState, SamwinUserAssistanceState.getMyAssistanceState])
   public static isConferencing(state: SamwinUserAssistanceStateModel, myAssistanceState: UserAssistanceStateRealTimeData): boolean {
      return myAssistanceState?.state === AssistanceState.Conference || state.desiredState === AssistanceState.Conference;
   }

   @Selector([SamwinUserAssistanceState, SamwinUserAssistanceState.getMyAssistanceState, AgentDetailsBehaviourState.getCurrentSelectedAgentId])
   public static isConferencingWithSelectedAgent(
      state: SamwinUserAssistanceStateModel,
      myAssistanceState: UserAssistanceStateRealTimeData,
      selectedAgentId: number
   ): boolean {
      return (
         (myAssistanceState?.userId === selectedAgentId || state.desiredUserId === selectedAgentId) &&
         (myAssistanceState?.state === AssistanceState.Conference || state.desiredState === AssistanceState.Conference)
      );
   }

   @Action(UpdateDesiredAssistanceState)
   public updateDesiredAssistanceState(ctx: StateContext<SamwinUserAssistanceStateModel>, action: UpdateDesiredAssistanceState) {
      ctx.patchState({
         desiredState: action.desiredState,
      });
   }

   @Action(UpdateUserAssistanceStates)
   public updateSingleUserAssistanceState(ctx: StateContext<SamwinUserAssistanceStateModel>, action: UpdateUserAssistanceStates) {
      const newUserAssistanceStates: UserAssistanceStatesRealTimeData = action.data;
      const state = ctx.getState();
      const newAssistanceStates = _.cloneDeep(state.assistanceStates);
      const myUserId = this.store.selectSnapshot(SessionState.getAuthenticatedUserId);

      _.forEach(newUserAssistanceStates.assistanceStates, (newUserAssistanceState) => {
         newAssistanceStates[newUserAssistanceState.userId] = newUserAssistanceState;

         const resetDesired =
            (newUserAssistanceState.assistingUserId === myUserId &&
               (newUserAssistanceState.state === state.desiredState || newUserAssistanceState.state === AssistanceState.None)) ||
            (newUserAssistanceState.userId === state.desiredUserId && newUserAssistanceState.assistingUserId !== myUserId);
         if (resetDesired) {
            ctx.patchState({
               desiredUserId: null,
               desiredState: null,
            });
         }
      });

      ctx.patchState({
         assistanceStates: newAssistanceStates,
      });
   }
}
